'use client';

import { PrismicRichText } from "@prismicio/react";
import { PrismicNextImage } from "@prismicio/next";
import { isFilled } from "@prismicio/client";
import { Splide, SplideSlide } from '@splidejs/react-splide';
import '@splidejs/react-splide/css';

/**
 * @typedef {import("@prismicio/client").Content.ProjectInfoSlice} ProjectInfoSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<ProjectInfoSlice>} ProjectInfoProps
 * @type {import("react").FC<ProjectInfoProps>}
 */
const ProjectInfo = ({ slice }) => {
  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="py-20"
    >
      <div className="max-w-400 mx-auto w-full">
        {isFilled.richText(slice.primary.title) && (
          <PrismicRichText
            field={slice.primary.title}
            components={{
              heading1: ({ children }) => <h1 className="h1-project mb-10 px-8 md:px-16">{children}</h1>,
            }}
          />
        )}
        <div className="flex flex-col lg:flex-row items-stretch">
          {/* Image principale desktop */}
          {isFilled.image(slice.primary.img) && (
            <div className="hidden md:block w-full max-h-140">
              <PrismicNextImage
                field={{
                  ...slice.primary.img,
                  alt: slice.primary.img.alt || "Image principale"
                }}
                className="w-full h-full max-h-140 object-cover"
              />
            </div>
          )}
          {/* Image principale mobile */}
          {isFilled.image(slice.primary.img_mobile) && (
            <div className="block md:hidden w-full max-h-60">
              <PrismicNextImage
                field={{
                  ...slice.primary.img_mobile,
                  alt: slice.primary.img_mobile.alt || "Image principale mobile"
                }}
                className="w-full h-full max-h-60 object-cover"
              />
            </div>
          )}
          <div className="flex flex-col px-8 md:px-16 xl:pr-24 xl:pl-20 max-w-150 xl:max-w-180 w-full gap-8 py-10 md:py-20 lg:py-5 justify-center mx-auto">
            <div className="flex flex-col gap-2">
              {isFilled.keyText(slice.primary.adress) && (
                <p className="text-base-desktop !font-medium">{slice.primary.adress}</p>
              )}

              {isFilled.richText(slice.primary.text) && (
                <PrismicRichText
                  field={slice.primary.text}
                  components={{
                    paragraph: ({ children }) => <p className="text-base-desktop ">{children}</p>,
                    strong: ({ children }) => <strong>{children}</strong>,
                    em: ({ children }) => <em>{children}</em>,
                    hyperlink: ({ node, children }) => (
                      <a
                        href={node.data.url}
                        target={node.data.target || "_self"}
                        rel={node.data.target === "_blank" ? "noopener noreferrer" : undefined}
                        className="a-underline"
                      >
                        {children}
                      </a>
                    ),
                  }}
                />
              )}
            </div>
            {slice.primary.table?.body?.rows && slice.primary.table.body.rows.length > 0 && (
              <table className="w-full">
                <tbody>
                  {slice.primary.table.body.rows.map((row, rowIndex) => (
                    <tr key={rowIndex} className="project-table">
                      {row.cells.map((cell, cellIndex) => (
                        <td key={cellIndex}>
                          {cell.content?.[0]?.text || ''} 
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>



        {isFilled.group(slice.primary.carrousel) && (
          <>
            {/* Mobile: scroll snap */}
            <div className="my-7 md:hidden overflow-x-auto snap-x snap-mandatory scroll-smooth flex gap-4 scrollbar-hide">
              {slice.primary.carrousel.map((item, index) => (
                <div key={index} className="snap-always snap-center shrink-0 w-[70dvw] h-[400px]">
                  {isFilled.image(item.img_mobile) && (
                    <PrismicNextImage
                      field={{
                        ...item.img_mobile,
                        alt: item.img_mobile.alt || `Image carrousel mobile ${index + 1}`
                      }}
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>
              ))}
            </div>

            {/* Desktop: Splide carrousel */}
            <div className="my-7 hidden md:block overflow-hidden">
              <Splide
                options={{
                  type: 'loop',
                  perPage: 5,
                  perMove: 1,
                  gap: '1.75rem',
                  pagination: false,
                  arrows: false,
                  autoplay: true,
                  interval: 2000,
                  speed: 800,
                  pauseOnHover: true,
                  height: '350px',
                  breakpoints: {
                    768: {
                      perPage: 2,
                    },
                    1024: {
                      perPage: 3,
                    },
                    1280: {
                      perPage: 4,
                    },
                  },
                }}
              >
                {slice.primary.carrousel.map((item, index) => (
                  <SplideSlide key={index}>
                    {isFilled.image(item.img) && (
                      <div className="w-full h-full">
                        <PrismicNextImage
                          field={{
                            ...item.img,
                            alt: item.img.alt || `Image carrousel ${index + 1}`
                          }}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                  </SplideSlide>
                ))}
              </Splide>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default ProjectInfo;
